import { useMemo } from 'react';
import { useInfiniteOwnedUserImages } from '@/hooks/content-generator/useInfiniteOwnedUserImages';
import { useInfiniteUserPages } from '@/hooks/content-generator/useInfiniteUserPages';
import { useInfiniteUserResearch } from '@/hooks/content-generator/useInfiniteUserResearch';
import { useUniversalStorageItems } from '@/hooks/universal-storage/useUniversalLibraryItems';

interface UseStorageContentImportProps {
  contentType: 'images' | 'research' | 'pages';
  searchQuery: string;
  limit?: number;
}

/**
 * Hook for importing content to Storage with smart filtering
 * Reuses existing infinite hooks but excludes items already in Storage
 */
export const useStorageContentImport = ({
  contentType,
  searchQuery,
  limit = 50
}: UseStorageContentImportProps) => {
  // Get current storage items to filter out duplicates
  const { data: storageItems = [] } = useUniversalStorageItems();

  // Fetch data based on content type (no project exclusion for storage)
  // Use owned images only - don't show images from other workbooks
  const imageQuery = useInfiniteOwnedUserImages({
    searchQuery,
    limit,
    // No excludeProjectId for storage - we want all owned images
  });

  const pageQuery = useInfiniteUserPages({
    searchQuery,
    limit,
    // No excludeProjectId for storage - we want all user pages
  });

  const researchQuery = useInfiniteUserResearch({
    searchQuery,
    limit,
    // No excludeProjectId for storage - we want all user research
  });

  // Use the appropriate query based on content type
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = contentType === 'pages' ? pageQuery : 
      contentType === 'research' ? researchQuery : 
      imageQuery;

  // Flatten all pages into a single array
  const allItems = useMemo(() => {
    return data?.pages.flatMap(page => page.data) || [];
  }, [data]);

  // Filter out items already in storage based on content type
  const filteredItems = useMemo(() => {
    if (!storageItems.length) return allItems;

    // Determine the storage content type for filtering
    const storageContentType = contentType === 'images' ? 'private_image' :
                              contentType === 'research' ? 'private_research' :
                              'private_page'; // pages maps to 'private_page' in storage

    // Get IDs of items already in storage for this content type
    const existingItemIds = storageItems
      .filter(item => item.content_type === storageContentType)
      .map(item => {
        // Map to the appropriate foreign key based on content type
        if (storageContentType === 'private_image') return item.workbook_image_id;
        if (storageContentType === 'private_research') return item.workbook_research_id;
        if (storageContentType === 'private_page') return item.workbook_page_id;
        return null;
      })
      .filter(Boolean); // Remove any null values

    console.log('[useStorageContentImport] Filtering items:', {
      contentType,
      storageContentType,
      totalItems: allItems.length,
      existingInStorage: existingItemIds.length,
      existingIds: existingItemIds
    });

    // Filter out items that are already in storage
    const filtered = allItems.filter(item => !existingItemIds.includes(item.id));

    console.log('[useStorageContentImport] Filtered results:', {
      originalCount: allItems.length,
      filteredCount: filtered.length,
      removedCount: allItems.length - filtered.length
    });

    return filtered;
  }, [allItems, storageItems, contentType]);

  // Total count from the first page - handle different data structures
  const totalCount = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) return 0;
    const firstPage = data.pages[0];
    return (firstPage as any)?.totalCount || (firstPage as any)?.total || filteredItems.length;
  }, [data, filteredItems.length]);

  return {
    data: filteredItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount,
    error
  };
}; 